import React, {useState} from 'react'
import {SubmitHandler, UseFormReturn} from 'react-hook-form'
import {Box, Button, Flex, Stack, Text} from '../../../../vanilla'
import {useStoreFinderSearchBarFields} from '../../../../hooks/forms/useStoreFinderSearchBarFields'
import {Field} from '../../../../components/field'
import {dividerBox, searchFieldWrapper, searchFormWrapper, browseAllStoresButton, browseAllStoresButtonText} from './style.css'
import {BaseButton} from '../../../../vanilla/components/BaseButton'
import {CrossHairsIcon} from '../../../../components/icons'
import {StoreFinderSearchBarFormFields} from '../../../../types/forms'
import {Spinner} from '../../../../components/spinner'
import {useHistory} from 'react-router-dom'


interface SearchStoreBoxProps {
  form: UseFormReturn<StoreFinderSearchBarFormFields>
  landingLayout: boolean
  onSubmit: SubmitHandler<StoreFinderSearchBarFormFields>
  handleUseMyLocationClick: () => void
}

export const SearchStoreBox = (props: SearchStoreBoxProps) => {
  const {landingLayout, form, onSubmit, handleUseMyLocationClick} = props

  const fields = useStoreFinderSearchBarFields({form})
  const [isLoading, setIsLoading] = useState(false)
  const history = useHistory()


  const handleBrowseAllStores = async () => {
    history.push('/store-finder/all-stores')
  }

  return (
    <Stack spacing="32px" bg="white" width="full">
      <Stack
        display={['none', 'flex']}
        paddingX="16px"
        spacing="16px"
        borderBottom="1px"
        borderColor="gray200"
        paddingY={landingLayout ? '28px' : '16px'}
        align={landingLayout ? 'center' : 'flex-start'}
      >
        <Text
          as="h3"
          variant="heading1"
          color={landingLayout ? 'accent0' : 'gray800'}
          align={landingLayout ? 'center' : 'left'}
          lineHeight="shorter"
          data-test-selector="store-finder-welcome-heading"
        >
          Welcome to Iceland Store Finder
        </Text>
        <Text
          as="span"
          style={{maxWidth: 345}}
          variant="text3"
          align={landingLayout ? 'center' : 'left'}
          lineHeight="shorter"
          data-test-selector="store-finder-welcome-text"
        >
          The Iceland store finder will help you find Iceland stores all over the country.
        </Text>
      </Stack>
      <Stack spacing="0px" paddingTop={['16px', '0px']} paddingX={['16px', '0px']}>
        {!landingLayout && (
          <Text
            display={['none', 'block']}
            variant="heading1"
            lineHeight="none"
            paddingX="16px"
            marginBottom="12px"
            data-test-selector="store-finder-search-again-heading"
          >
            Search again
          </Text>
        )}
        <Stack width="full" spacing="24px">
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Flex
              direction={['column', 'row']}
              gap={['12px', '8px']}
              align={['stretch', 'flex-start']}
              paddingX={['0px', '16px']}
              className={landingLayout ? searchFormWrapper : undefined}
            >
              <Box width="full" className={searchFieldWrapper} flexShrink="0">
                <Field {...fields.storeFinder} />
              </Box>
              <Button type="submit" variant="primary" size="sm" flexGrow="1" data-test-selector="store-finder-search-button">
                Find Stores
              </Button>
            </Flex>
          </form>
          <Stack paddingBottom={['0px', landingLayout ? '24px' : '0px']}>
            <Box className={dividerBox}>
              <Text
                as="span"
                variant="text1"
                position="absolute"
                paddingX="8px"
                lineHeight="none"
                bg="white"
                style={{top: -8, left: '50%', color: '#9b9b9b', transform: 'translateX(-50%)'}}
              >
                or
              </Text>
            </Box>
            <BaseButton
              variant="link"
              paddingX="0px"
              onClick={async () => {
                setIsLoading(true)
                await handleUseMyLocationClick()
                setIsLoading(false)
              }}
              display="inline-flex"
              gap="8px"
              alignItems="center"
              marginBottom="16px"
              height="auto"
            >
              {!isLoading ?
              (<>
                <CrossHairsIcon width="20px" height="24px" style={{color: '#9b9b9b'}} />
                <Text
                  variant="unstyled"
                  fontWeight="normal"
                  fontSize="sm"
                  lineHeight="inherit"
                  color="gray800"
                  data-test-selector="store-finder-use-my-location-button"
                >
                  Use my current location
                </Text>
              </>)
              : (<Spinner color='primary' size='sm' />)}
            </BaseButton>
            <BaseButton
              variant="link"
              onClick={handleBrowseAllStores}
              className={browseAllStoresButton}
            >
              <Text 
                variant="text3"
                className={browseAllStoresButtonText}
                data-test-selector="store-finder-browse-all-stores-button"
                >
                  Alternatively, you can browse all stores
              </Text>
            </BaseButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
